import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SlidersHorizontal,
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  AlertTriangle,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, FitThumb, LoadingState, ErrorState, EmptyState, Button } from '@/components/ui';
import { PickerSheet, PickerRow } from '@/features/wardrobe/components/PickerSheet';
import { LogWearModal, uploadWearPhotos, type LogWearValues } from './components/LogWearModal';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { SEASONS } from '@/lib/constants';
import { formatDate, formatRelativeDate, formatWearCount } from '@/lib/utils';
import type { Fit } from '@/lib/types';

type SortKey = 'name' | 'created' | 'lastWorn' | 'wearCount';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'created', label: 'Created Date' },
  { key: 'lastWorn', label: 'Last Worn' },
  { key: 'wearCount', label: 'Wear Count' },
];

const WEAR_FILTERS = ['All', 'Never Worn', '1-5', '6-10', '10+'] as const;

export function FitsAvailablePage() {
  const { data, loading, error, refetch } = useApi<{ fits: Fit[] }>('/fits');
  const [showFilter, setShowFilter] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [compact, setCompact] = useState(false);
  const [sort, setSort] = useState<SortKey>('created');
  const [seasonFilter, setSeasonFilter] = useState<string>('All');
  const [wearFilter, setWearFilter] = useState<(typeof WEAR_FILTERS)[number]>('All');
  const [wearFit, setWearFit] = useState<Fit | null>(null);

  const fits = useMemo(() => {
    let list = data?.fits ?? [];
    if (seasonFilter !== 'All') {
      list = list.filter((f) => f.seasons.includes(seasonFilter as Fit['seasons'][number]));
    }
    if (wearFilter !== 'All') {
      list = list.filter((f) => {
        const c = f.wearCount ?? 0;
        if (wearFilter === 'Never Worn') return c === 0;
        if (wearFilter === '1-5') return c >= 1 && c <= 5;
        if (wearFilter === '6-10') return c >= 6 && c <= 10;
        return c > 10;
      });
    }
    return [...list].sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastWorn':
          if (!a.lastWornAt) return 1;
          if (!b.lastWornAt) return -1;
          return new Date(b.lastWornAt).getTime() - new Date(a.lastWornAt).getTime();
        case 'wearCount':
          return (b.wearCount ?? 0) - (a.wearCount ?? 0);
        case 'created':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  }, [data, seasonFilter, wearFilter, sort]);

  const handleLogWear = async (values: LogWearValues, photos: string[]): Promise<string | null> => {
    if (!wearFit) return null;
    const res = await api.post<{ id: string }>(`/fits/${wearFit.id}/wears`, {
      wornDate: values.wornDate,
      occasion: values.occasion || undefined,
      locationText: values.locationText || undefined,
      notes: values.notes || undefined,
    });
    if (!res?.id) return null;
    if (photos.length) await uploadWearPhotos(res.id, photos);
    refetch();
    return res.id;
  };

  return (
    <Page>
      <PageHeader section="Available Fits" backHref="/fits" backLabel="Fits" />

      {/* Filter / sort / view bar */}
      <div className="sticky top-[64px] z-10 bg-background/90 backdrop-blur border-b border-border/50">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:border-primary/30 transition-all"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filter
          </button>
          <button
            onClick={() => setShowSort(true)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
              sort !== 'created'
                ? 'bg-primary text-primary-foreground border border-primary'
                : 'bg-card border border-border text-foreground hover:border-primary/30'
            }`}
          >
            <ArrowUpDown className="w-4 h-4" />
            {sort !== 'created' ? (SORTS.find((s) => s.key === sort)?.label ?? 'Sort') : 'Sort'}
          </button>
          {sort !== 'created' && (
            <button
              onClick={() => setSort('created')}
              title="Clear sort"
              aria-label="Clear sort"
              className="flex items-center px-2.5 py-2 bg-card border border-border rounded-xl text-foreground hover:border-primary/30 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">{fits.length} fits</span>
          <button
            onClick={() => setCompact((c) => !c)}
            className="p-2 bg-card border border-border rounded-xl text-foreground hover:border-primary/30 transition-all"
            aria-label="Toggle view"
          >
            {compact ? <LayoutGrid className="w-5 h-5" /> : <LayoutList className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <Main className="space-y-3">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {!loading && !error && fits.length === 0 && (
          <EmptyState
            icon={<LayoutGrid className="w-6 h-6" />}
            title="No fits found"
            description="Build your first outfit or adjust your filters."
            action={
              <Link to="/fits/builder">
                <Button>Build a Fit</Button>
              </Link>
            }
          />
        )}

        {fits.map((fit) => {
          const unavailable = fit.unavailablePieces ?? [];
          const hasWarning = unavailable.length > 0;
          return (
            <Card key={fit.id} className="p-3">
              <div className="flex gap-3">
                <div className="relative flex-shrink-0">
                  <FitThumb url={fit.imageUrl} piecePhotoUrls={fit.piecePhotoUrls} alt={fit.name} className="w-20 h-20 rounded-xl" />
                  {hasWarning && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-3 h-3 text-accent-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground leading-tight">{fit.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {fit.pieceCount ?? 0} pieces
                  </p>

                  {!compact ? (
                    <>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {fit.seasons.map((s) => (
                          <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground capitalize">
                            {s}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">Wears</span>
                          <span className="text-xs text-foreground font-medium">{fit.wearCount ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">Last Worn</span>
                          <span className="text-xs text-foreground font-medium">{formatRelativeDate(fit.lastWornAt ?? null)}</span>
                        </div>
                      </div>
                      {hasWarning && (
                        <div className="mt-2 flex items-start gap-2 bg-accent/10 rounded-lg p-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-muted-foreground leading-tight">
                            {unavailable.length} piece{unavailable.length > 1 ? 's' : ''} unavailable:{' '}
                            {unavailable.map((p) => p.name).join(', ')}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {formatWearCount(fit.wearCount ?? 0)} ·{' '}
                        {fit.lastWornAt ? `Last: ${formatDate(fit.lastWornAt)}` : 'Never worn'}
                      </span>
                      {hasWarning && <AlertTriangle className="w-3.5 h-3.5 text-accent" />}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 mt-3 pt-3 border-t border-border">
                <Link to={`/fits/${fit.id}`} className="text-xs font-medium text-primary hover:opacity-80">
                  View Details
                </Link>
                <button onClick={() => setWearFit(fit)} className="text-xs font-medium text-primary hover:opacity-80">
                  Log Wear
                </button>
                <Link to={`/fits/builder?edit=${fit.id}`} className="text-xs font-medium text-primary hover:opacity-80">
                  Edit
                </Link>
              </div>
            </Card>
          );
        })}
      </Main>

      {/* Filter sheet */}
      <PickerSheet
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        title="Filter"
        footer={
          <Button
            fullWidth
            variant="outline"
            onClick={() => {
              setSeasonFilter('All');
              setWearFilter('All');
            }}
          >
            Clear All
          </Button>
        }
      >
        <div className="px-2 space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Season</p>
            <div className="flex flex-wrap gap-2">
              {['All', ...SEASONS.map((s) => s.id)].map((id) => (
                <button
                  key={id}
                  onClick={() => setSeasonFilter(id)}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-all ${
                    seasonFilter === id ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'
                  }`}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Wears</p>
            <div className="flex flex-wrap gap-2">
              {WEAR_FILTERS.map((w) => (
                <button
                  key={w}
                  onClick={() => setWearFilter(w)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    wearFilter === w ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PickerSheet>

      {/* Sort sheet */}
      <PickerSheet isOpen={showSort} onClose={() => setShowSort(false)} title="Sort By">
        <div className="space-y-1">
          {SORTS.map((s) => (
            <PickerRow
              key={s.key}
              label={s.label}
              selected={sort === s.key}
              onClick={() => {
                setSort(s.key);
                setShowSort(false);
              }}
            />
          ))}
        </div>
      </PickerSheet>

      {wearFit && (
        <LogWearModal
          isOpen
          onClose={() => setWearFit(null)}
          fitName={wearFit.name}
          pieceCount={wearFit.pieceCount ?? 0}
          defaultLocation=""
          onSubmit={handleLogWear}
        />
      )}
    </Page>
  );
}
